import { useRef } from "react";
import { X } from "lucide-react";
import { cn } from "../../../shared/lib";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/ui/shadcnSelect";
import {
  CHANNEL_OPTIONS,
  COUNTRY_OPTIONS,
  CURRENCY_OPTIONS,
  MESSAGE_KEYWORDS,
  SEGMENT_OPTIONS,
} from "../constants/notificationData";
import {
  AudienceFilters,
  AudienceSegment,
  ComposeBroadcastPayload,
  NotificationChannel,
} from "../types";
import AudienceCustomerPicker from "./AudienceCustomerPicker";

const MESSAGE_MAX_LENGTH = 500;

type NotificationComposeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: string;
  onSubjectChange: (value: string) => void;
  message: string;
  onMessageChange: (value: string) => void;
  channel: NotificationChannel;
  onChannelChange: (value: NotificationChannel) => void;
  audience: AudienceFilters;
  onAudienceChange: (value: AudienceFilters) => void;
  isSending?: boolean;
  isEditingDraft?: boolean;
  onSend: (payload: ComposeBroadcastPayload) => void;
  onSaveDraft: (payload: ComposeBroadcastPayload) => void;
  onDeleteDraft?: () => void;
};

const NotificationComposeModal = ({
  open,
  onOpenChange,
  subject,
  onSubjectChange,
  message,
  onMessageChange,
  channel,
  onChannelChange,
  audience,
  onAudienceChange,
  isSending,
  isEditingDraft,
  onSend,
  onSaveDraft,
  onDeleteDraft,
}: NotificationComposeModalProps) => {
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const isCustomerTarget = audience.targetType === "customer";

  const canSend =
    Boolean(subject.trim()) &&
    Boolean(message.trim()) &&
    (!isCustomerTarget || audience.customers.length > 0) &&
    !isSending;

  const canSaveDraft = (Boolean(subject.trim()) || Boolean(message.trim())) && !isSending;

  const insertKeyword = (token: string) => {
    const el = messageRef.current;
    const start = el?.selectionStart ?? message.length;
    const end = el?.selectionEnd ?? message.length;
    const next = `${message.slice(0, start)}${token}${message.slice(end)}`;

    onMessageChange(next);

    requestAnimationFrame(() => {
      el?.focus();
      const cursor = start + token.length;
      el?.setSelectionRange(cursor, cursor);
    });
  };

  const buildPayload = (): ComposeBroadcastPayload => ({
    subject: subject.trim(),
    message: message.trim(),
    channel,
    audience,
  });

  const handleSend = () => {
    if (!canSend) return;
    onSend(buildPayload());
  };

  const handleSaveDraft = () => {
    if (!canSaveDraft) return;
    onSaveDraft(buildPayload());
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} contentClassName="max-w-[600px]">
      <ModalHeader>
        <ModalTitle>{isEditingDraft ? "Edit draft" : "New broadcast"}</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSending}
          onClick={() => onOpenChange(false)}
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="pt-4">
        <div className="space-y-6">
          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">Channel</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {CHANNEL_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.comingSoon || isSending}
                  onClick={() => onChannelChange(option.value)}
                  className={cn(
                    "flex items-center gap-2 rounded-full border px-4 py-2 text-[14px] font-[450] transition-colors",
                    channel === option.value
                      ? "border-[#5B21FF] bg-[#EEF1FB] text-[#2C2F44]"
                      : "border-[#E1E6FF] text-[#6F739B]",
                    option.comingSoon && "cursor-not-allowed opacity-50",
                  )}
                >
                  {option.label}
                  {option.comingSoon ? (
                    <span className="rounded-full bg-[#F7F8FD] px-2 py-0.5 text-[11px] text-[#6F739B]">
                      Coming soon
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">Send to</div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                disabled={isSending}
                onClick={() => onAudienceChange({ ...audience, targetType: "segment" })}
                className={cn(
                  "flex-1 rounded-[12px] border px-4 py-2 text-[14px] font-[450] transition-colors",
                  audience.targetType === "segment"
                    ? "border-[#5B21FF] bg-[#EEF1FB] text-[#2C2F44]"
                    : "border-[#E1E6FF] text-[#6F739B]",
                )}
              >
                Customer segment
              </button>
              <button
                type="button"
                disabled={isSending}
                onClick={() => onAudienceChange({ ...audience, targetType: "customer" })}
                className={cn(
                  "flex-1 rounded-[12px] border px-4 py-2 text-[14px] font-[450] transition-colors",
                  audience.targetType === "customer"
                    ? "border-[#5B21FF] bg-[#EEF1FB] text-[#2C2F44]"
                    : "border-[#E1E6FF] text-[#6F739B]",
                )}
              >
                Specific customer
              </button>
            </div>
          </div>

          {isCustomerTarget ? (
            <div>
              <div className="text-[14px] font-[450] text-[#2C2F44]">Customers</div>
              <AudienceCustomerPicker
                value={audience.customers}
                onChange={(customers) => onAudienceChange({ ...audience, customers })}
              />
            </div>
          ) : (
            <>
              <div>
                <div className="text-[14px] font-[450] text-[#2C2F44]">Segment</div>
                <Select
                  value={audience.segment}
                  onValueChange={(value) =>
                    onAudienceChange({ ...audience, segment: value as AudienceSegment })
                  }
                >
                  <SelectTrigger className="mt-2 h-9 w-full rounded-[12px] bg-[#EEF1FB] px-4 py-2 text-[14px] font-[450]">
                    <SelectValue placeholder="Select segment" />
                  </SelectTrigger>
                  <SelectContent className="border-none bg-white">
                    <SelectGroup>
                      {SEGMENT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[14px] font-[450] text-[#2C2F44]">Country</div>
                  <Select
                    value={audience.country}
                    onValueChange={(value) => onAudienceChange({ ...audience, country: value })}
                  >
                    <SelectTrigger className="mt-2 h-9 w-full rounded-[12px] bg-[#EEF1FB] px-4 py-2 text-[14px] font-[450]">
                      <SelectValue placeholder="All countries" />
                    </SelectTrigger>
                    <SelectContent className="border-none bg-white">
                      <SelectGroup>
                        {COUNTRY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="text-[14px] font-[450] text-[#2C2F44]">Currency</div>
                  <Select
                    value={audience.currency}
                    onValueChange={(value) => onAudienceChange({ ...audience, currency: value })}
                  >
                    <SelectTrigger className="mt-2 h-9 w-full rounded-[12px] bg-[#EEF1FB] px-4 py-2 text-[14px] font-[450]">
                      <SelectValue placeholder="All currencies" />
                    </SelectTrigger>
                    <SelectContent className="border-none bg-white">
                      <SelectGroup>
                        {CURRENCY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">Subject</div>
            <Input
              value={subject}
              disabled={isSending}
              onChange={(event) => onSubjectChange(event.target.value)}
              placeholder="Enter subject"
              className="mt-2 h-12 rounded-[14px]"
            />
          </div>

          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">Message</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {MESSAGE_KEYWORDS.map((keyword) => (
                <button
                  key={keyword.token}
                  type="button"
                  disabled={isSending}
                  onClick={() => insertKeyword(keyword.token)}
                  className="rounded-full bg-[#EEF1FB] px-3 py-1 text-[12px] font-[450] text-[#2C2F44] transition-colors hover:bg-[#E1E6FF] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  + {keyword.label}
                </button>
              ))}
            </div>
            <textarea
              ref={messageRef}
              value={message}
              disabled={isSending}
              maxLength={MESSAGE_MAX_LENGTH}
              onChange={(event) => onMessageChange(event.target.value)}
              placeholder="Write the broadcast message. Use the keywords above to personalize it, e.g. Hi {{firstName}}"
              className="mt-2 min-h-[140px] w-full resize-none rounded-[14px] bg-[#EEF1FB] px-4 py-3 text-[14px] font-[350] text-gray-30 placeholder:text-gray-20 focus:outline-none focus:ring-2 focus:ring-[#D9DEFF] disabled:cursor-not-allowed disabled:opacity-60"
            />
            <div className="mt-2 text-right text-[13px] font-[350] text-[#6F739B]">
              {message.length}/{MESSAGE_MAX_LENGTH}
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="justify-between">
        {isEditingDraft && onDeleteDraft ? (
          <Button
            variant="ghost"
            disabled={isSending}
            className="h-11 rounded-[14px] px-4 text-[#F73C3C] hover:bg-[#FDEAEA]"
            onClick={onDeleteDraft}
          >
            Delete draft
          </Button>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            disabled={isSending}
            className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={!canSaveDraft}
            className="h-11 rounded-[14px] px-6"
            onClick={handleSaveDraft}
          >
            {isSending ? "Saving..." : "Save as draft"}
          </Button>
          <Button
            className="h-11 rounded-[14px] px-6"
            disabled={!canSend}
            onClick={handleSend}
          >
            {isSending ? "Sending..." : "Send broadcast"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default NotificationComposeModal;
